import { PremiumCurrencyFormatter } from '@wix/premium-react-text';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { Country, PaymentCycle } from 'enums';
import { RootStore, withInjectedRootStore } from 'providers';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CycleDetails } from 'types';
import { isCycleOfMonths } from 'utils';
import { Box, Text } from '@wix/design-system';
import { CycleMultiplier } from '../../../constants';
import s from './Cycles.scss';
import { FullPrice } from './MonthlyFullPrice';

export interface CyclePriceProps extends InjectedExperimentsProps {
  country: Country;
  isInSale: boolean;
  rootStore: RootStore;
  cycleDetails: CycleDetails;
}

const CyclePriceComp: React.FunctionComponent<CyclePriceProps> = ({
  country,
  isInSale,
  rootStore,
  cycleDetails,
}) => {
  const {
    fullTermDisplayPrice: fullPrice,
    monthlyFullPrice,
    fullTermActualPrice: actualPrice,
    cycleId,
    firstPeriodPriceMonthly,
    monthlyActualPrice,
  } = cycleDetails;

  const {
    isSaleProduct,
    appContext: { isLimitedTimeDiscount },
    isEligibleForRevShare
  } = rootStore.appStore;

  const isBrazil = country === Country.BR;

  const priceTextWeight = !isBrazil && isInSale ? 'normal' : 'thin';

  const shouldShowOriginalFullPeriodPrice =
    isBrazil && isInSale && fullPrice !== actualPrice;

  const shouldShowBrazilPrice = isBrazil && cycleId !== PaymentCycle.MONTHLY;

  const isLimitedTimeOfferingMode =
    isLimitedTimeDiscount && !!firstPeriodPriceMonthly && !isBrazil;

  const isInSaleMode = isSaleProduct && !isBrazil;

  const shouldShowOriginalMonthlyPrice =
    monthlyFullPrice !== monthlyActualPrice &&
    (isInSaleMode || isLimitedTimeOfferingMode);
  const monthlyFullPriceClassname = !isBrazil ? s.cycleSaleFullpriceText : '';

  const multiplierDirection = isBrazil ? 'vertical' : 'horizontal';

  return (
    <Text
      weight={priceTextWeight}
      className={s.cycleEntryPrice}
      size='medium'
      dataHook='cycle-price'
    >
      <Box direction={multiplierDirection}>
        <Box direction='horizontal'>

          {shouldShowOriginalFullPeriodPrice && (
            <FullPrice.BrazilSale fullPrice={fullPrice} />
          )}
          {shouldShowBrazilPrice && <FullPrice.Brazil actualPrice={actualPrice} />}
          {shouldShowOriginalMonthlyPrice && !isCycleOfMonths(cycleId) && (
            <FullPrice.Monthly
              monthlyFullPrice={monthlyFullPrice}
              className={monthlyFullPriceClassname}
            />
          )}
        </Box>

        <Box direction='horizontal' align='center' gap="SP1">
          <CycleMultiplierText cycleDetails={cycleDetails} country={country} />
          {isEligibleForRevShare && <CycleSavingsText cycleDetails={cycleDetails} />}
        </Box>
      </Box>
    </Text>
  );
};

const CycleMultiplierText: React.FC<{
  cycleDetails: CycleDetails;
  country: Country;
}> = ({
  cycleDetails: { cycleId, monthlyActualPrice, firstPeriodPriceMonthly, fullTermDisplaySavings },
  country,
}) => {
    const { t } = useTranslation();
    const isBrazil = country === Country.BR;
    const cyclePriceKey =
      cycleId === PaymentCycle.MONTHLY
        ? 'cycle_entry.cycle_price.MONTHLY'
        : isBrazil
          ? 'cycle_entry.cycle_price.br'
          : 'cycle_entry.cycle_price';

    return (
      <PremiumCurrencyFormatter
        render={(formatCurrency) =>
          t(cyclePriceKey, {
            price: formatCurrency(firstPeriodPriceMonthly || monthlyActualPrice),
            cycleLength: CycleMultiplier[cycleId],
          })
        }
      />
    );
  };

export const CycleSavingsText: React.FC<{
  cycleDetails: CycleDetails;
}> = ({
  cycleDetails: { fullTermDisplaySavings },
}) => {
    const { t } = useTranslation();

    if (!fullTermDisplaySavings) {
      return null;
    }

    return (
      <PremiumCurrencyFormatter
        currencySettingsOverride={{ fractionSize: 2 }}
        render={(formatCurrency) =>
          <Text className={s.savingsText}>
            {t('cycle_entry.cycle_discount.revshare', {
              amount: formatCurrency(fullTermDisplaySavings),
            })}
          </Text>
        }
      />
    );
  };

export const CyclePrice = withInjectedRootStore(
  withExperiments(CyclePriceComp),
);
